import { FileTypes } from "@/enums";
import { useAllRights } from "@/hooks";
import {
  useGetCDNFilePathQuery,
  useGetSasMutation,
  useUploadFileMutation,
} from "@/services";
import { faDownload, faInbox } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Upload } from "antd";
import { bool, object, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DynamicFormItem } from "../../DynamicFormItem";
import { useCheckEditableFormField } from "../../hooks/useCheckEditableFormField";
import { FullscreenModal } from "../../styled";
import {
  getFileListToUpload,
  getFiles,
  getUpdatedFieldValue,
  getUpdatedFileList,
} from "./helper";

import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
const { Dragger } = Upload;

const DynamicUpload = ({ formField, formDisabled, placeholderId }) => {
  const intl = useIntl();

  const { projectId } = useParams();

  const [fileList, setFileList] = useState(null);

  const [uploadFile] = useUploadFileMutation();
  const [getSas] = useGetSasMutation();

  const { property: formFieldProperty } = formField;
  const { isDisabled, formRef } = useCheckEditableFormField(formField);
  const { adminRights } = useAllRights();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState([]);

  const [fileId, setFileId] = useState(null);
  const { data: cdnFilePath } = useGetCDNFilePathQuery(
    {
      projectId,
      fileId,
    },
    { skip: !fileId }
  );

  const canEditUploads =
    adminRights.canEditCompletedProject || (!formDisabled && !isDisabled);
  useEffect(() => {
    if (!formRef.current) return;

    const value = formRef.current.getFieldValue(formFieldProperty.name);

    setFileList(getFiles(value, formFieldProperty.fieldType));
  }, [formFieldProperty.fieldType, formFieldProperty.name, formRef]);

  useEffect(() => {
    if (!formRef.current || !fileList) return;

    formRef.current.setFieldsValue({
      [formFieldProperty.name]: getUpdatedFieldValue(
        fileList,
        formFieldProperty.fieldType
      ),
    });
  }, [fileList, formFieldProperty.fieldType, formFieldProperty.name, formRef]);

  const uploadItem = ({ file, onProgress, onSuccess }) => {
    const storageInfo = { name: file.name };
    getSas({ storageInfo, intakeId: projectId || placeholderId })
      .unwrap()
      .then((response) => {
        onProgress({ percent: 10, loaded: 0, total: file.size });
        return uploadFile({
          file,
          location: response.data.itemPath,
          onProgress: ({ percent, loaded, total }) => {
            const newProgress = {
              percent: (percent / 100) * 90 + 10,
              loaded: loaded,
              total: total,
            };
            if (newProgress.percent === 100) {
              onSuccess(file);
              return;
            }
            onProgress(newProgress);
          },
        })
          .unwrap()
          .then(({ blockIds, location }) => {
            file.blobListIds = blockIds;
            file.blobUrl = location;
            file.imageUrl = file.type?.startsWith("image/") && location;
            file.url = `projects/${location}`;
            onSuccess(file);
          })
          .catch(() => {
            toast.error(intl.formatMessage({ id: "general.uploadFile" }));
          });
      });
  };

  const setPreviewDoc = (document) => {
    setFileId(document.url.replace("projects/" + projectId + "/", ""));
    setPreviewFile([document]);
  };

  const downloadAndCloseModal = (filePath) => {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (fileId && previewFile && cdnFilePath) {
      setIsModalOpen(true);
    }
  }, [fileId, previewFile, cdnFilePath]);

  const customValidator = () => ({
    validator: (_, value) => {
      const getRejectedPromise = (isUploading) =>
        Promise.reject(
          intl.formatMessage({
            id: isUploading
              ? "general.uploadsWaitForAllToComplete"
              : "general.uploadsFailedTryAgain",
          })
        );

      if (!value || !value.event) return Promise.resolve();

      return getRejectedPromise(true);
    },
  });

  return (
    <DynamicFormItem
      formFieldProperty={formFieldProperty}
      customValidator={customValidator}
    >
      <Dragger
        data-test-id="dynamic-upload-upload-element"
        fileList={fileList}
        disabled={!canEditUploads}
        customRequest={uploadItem}
        beforeUpload={(file) => {
          setFileList(
            getFileListToUpload(file, fileList, formFieldProperty.fieldType)
          );
        }}
        onRemove={({ uid }) => {
          setFileList(fileList?.filter((file) => file.uid !== uid));
        }}
        onChange={(info) => {
          setFileList(getUpdatedFileList(info));
        }}
        onPreview={(e) => setPreviewDoc(e)}
        accept={formFieldProperty.allowedFileTypes
          ?.filter((fileType) => !!FileTypes[fileType])
          .map((fileType) => FileTypes[fileType])
          .join(",")}
        isImageUrl={({ thumbUrl }) => !!thumbUrl}
        showUploadList={{
          showRemoveIcon: canEditUploads,
        }}
      >
        <p className="ant-upload-drag-icon">
          <FontAwesomeIcon icon={faInbox} size="xl" />
        </p>
        <p className="ant-upload-text">
          {intl.formatMessage({ id: "general.uploadFile" })}
        </p>
      </Dragger>
      {cdnFilePath && (
        <FullscreenModal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          centered
          onOk={() => downloadAndCloseModal(cdnFilePath)}
          okButtonProps={{ icon: <FontAwesomeIcon icon={faDownload} /> }}
          okText={intl.formatMessage({ id: "general.download" })}
        >
          <DocViewer
            documents={previewFile?.map((file) => ({
              uri: cdnFilePath,
              fileName: file.name,
            }))}
            pluginRenderers={DocViewerRenderers}
          />
        </FullscreenModal>
      )}
    </DynamicFormItem>
  );
};

DynamicUpload.propTypes = {
  formField: object.isRequired,
  formDisabled: bool,
  placeholderId: string,
};

export default DynamicUpload;
